import { isMachineUnlocked } from '../core/gacha';
import { MACHINES } from '../data/machines';
import { isCorruptedMachine } from './abyssApi';
import { overlayRoot } from './columnScale';
import { registerEscape } from './keyboard';
import { readJsonPref, removePref, writeJsonPref } from './prefs';
import { TIPS, TIP_IDS, tipDef, type TipDef, type TipId, type TipScreen } from './tipsData';
import type { ScreenCtx } from './types';

export type { TipId, TipScreen } from './tipsData';

/**
 * Info-bulles de première partie (phase 4, v0.37.0 — A78, A81).
 *
 * Sept bulles F1 à flèche (`tipsData.ts`), chacune montrée **une fois par
 * installation** : préférence locale `diceordie.tips.seen` (tableau d'ids JSON),
 * **hors sauvegarde**, remise à zéro dans le Debug. Marquée vue **dès qu'elle
 * s'affiche** : fermée par un toucher, une navigation ou un changement de phase,
 * elle ne revient pas. Aucune migration : les parties déjà commencées les voient
 * aussi (A81).
 *
 * ── Règles ───────────────────────────────────────────────────────────────
 *  - **Une seule bulle à la fois** (état du module) ; à un montage d'écran ou à
 *    un événement (`gold`, `purchase`), la **première de la liste** dont la
 *    condition est vraie est retenue, les autres attendent un montage ou un
 *    événement ultérieur. Une bulle retenue mais retardée (fenêtre ouverte,
 *    ancre pas encore posée) est réessayée toutes les 250 ms.
 *  - Jamais par-dessus une fenêtre `.f-modal` / `.f-scrim` (révélation, lore,
 *    Notes de dev, abandon) ni l'avis de mise à jour : elle attend leur
 *    fermeture. Une fenêtre qui s'ouvre sur une bulle affichée la retire.
 *  - **Fermeture** : tout `pointerdown` du document (phase de capture) la ferme
 *    **sans avaler l'événement** (le geste de lancer lance bien les dés, un
 *    toucher sur un onglet l'ouvre bien) ; `Échap` aussi (`registerEscape`).
 *    Aucune temporisation.
 *  - Posée dans `#app` (`overlayRoot`), en px de conception : le rectangle de
 *    l'ancre est ramené à l'échelle de la colonne (`toColumnBox`, phase 2).
 *    Repositionnée sur `resize`, `scroll` et à chaque battement ; retirée si
 *    l'ancre disparaît.
 *
 * Les écrans n'appellent que `mountTips(ctx, screen, el)` : toute la logique de
 * condition est ici. La partie pure (`tipApplies`, `pickTip`, `placeTip`,
 * `toColumnBox`, lecture / écriture des « vues ») est testée sans DOM.
 */

// ───────────────────────────────────────────────────────────── Persistance

/** Clé de la préférence locale (tableau JSON d'identifiants), hors sauvegarde. */
export const TIPS_SEEN_KEY = 'diceordie.tips.seen';

/**
 * Indications persistantes qui partagent la mémoire « vu » des info-bulles sans
 * être des bulles (A88-9) : `treeHint` = « Touchez un nœud pour voir son effet
 * et l'acheter. », cachée dès qu'un nœud a été touché. Même clé de stockage,
 * donc même remise à zéro (bouton Debug) ; absente de `TIPS` et de `TIP_IDS`.
 */
export type HintId = 'treeHint';
export const HINT_IDS: readonly HintId[] = ['treeHint'];

/**
 * Repli en mémoire : si le stockage est indisponible (mode privé), une bulle déjà
 * montrée ne revient pas non plus dans la même session.
 */
const memorySeen = new Set<string>();

/** Toutes les clés que la préférence peut contenir : bulles, puis indications. */
const KNOWN_IDS: readonly string[] = [...TIP_IDS, ...HINT_IDS];

function readSeenRaw(): Set<string> {
  const out = new Set<string>(memorySeen);
  const raw = readJsonPref<unknown>(TIPS_SEEN_KEY);
  if (Array.isArray(raw)) for (const id of raw) if (typeof id === 'string' && KNOWN_IDS.includes(id)) out.add(id);
  return out;
}

function markSeenRaw(id: string): void {
  memorySeen.add(id);
  const all = readSeenRaw();
  writeJsonPref(TIPS_SEEN_KEY, KNOWN_IDS.filter((i) => all.has(i)));
}

/** Bulles déjà montrées (stockage local + mémoire de la session). */
export function readSeen(): Set<TipId> {
  const all = readSeenRaw();
  return new Set(TIP_IDS.filter((id) => all.has(id)));
}

/** Marque une bulle vue (écrit tout de suite, sans attendre la fermeture). */
export function markSeen(id: TipId): void {
  markSeenRaw(id);
}

/** Une indication persistante a-t-elle déjà été vue (ou « consommée ») ? */
export function isHintSeen(id: HintId): boolean {
  return readSeenRaw().has(id);
}

/** Marque une indication persistante vue (mémorisée par installation). */
export function markHintSeen(id: HintId): void {
  markSeenRaw(id);
}

/** Remise à zéro (bouton du Debug) : les bulles et indications pourront revenir. */
export function resetSeen(): void {
  memorySeen.clear();
  removePref(TIPS_SEEN_KEY);
}

// ──────────────────────────────────────────────────────────── Conditions pures

/** Ce que les conditions lisent : un instantané plat, fabricable dans un test. */
export interface TipFacts {
  screen: TipScreen;
  /** Phase du jeu (`expedition`, `invasion`, `accalmie`…). */
  phase: string;
  /** `stats.throws` : lancers de toute la partie. */
  throws: number;
  /** `stats.runs` : runs terminées (défaite ou abandon). */
  runs: number;
  gold: number;
  /** Prix courant (remise comprise) de la machine la moins chère accessible. */
  cheapestMachine: number | null;
  invasionReady: boolean;
  /** Des dés Maîtres viennent d'être obtenus à la boutique (événement `purchase`). */
  diceJustBought: boolean;
  /** Dés Maîtres de l'inventaire hors des emplacements. */
  unequippedMasters: number;
  /** Dés Maîtres dans les emplacements. */
  equippedMasters: number;
  /** Le bouton « Aller au devant » est présent (affiché). */
  earlyButton: boolean;
}

/** La condition de la bulle est-elle vraie ? (l'écran est supposé monté) */
export function tipApplies(id: TipId, f: TipFacts): boolean {
  switch (id) {
    case 'throw':
      return f.screen === 'combat' && f.phase === 'expedition' && f.throws === 0;
    case 'recap':
      return f.screen === 'expeditionEnd';
    case 'tree':
      return f.screen === 'accalmie';
    case 'shop':
      return f.screen === 'accalmie' && f.cheapestMachine !== null && f.gold >= f.cheapestMachine;
    case 'equip':
      return f.screen === 'accalmie' && f.diceJustBought && f.unequippedMasters > 0;
    case 'invasion':
      return f.screen === 'accalmie' && f.invasionReady;
    case 'early':
      return f.screen === 'accalmie' && f.runs >= 1 && f.equippedMasters >= 2 && f.earlyButton;
  }
}

/**
 * La bulle à montrer : la première de la liste (ordre de priorité) qui est de
 * cet écran, pas encore vue, et dont la condition est vraie. `null` sinon.
 */
export function pickTip(f: TipFacts, seen: ReadonlySet<TipId>): TipId | null {
  for (const def of TIPS) {
    if (def.screen !== f.screen || seen.has(def.id)) continue;
    if (tipApplies(def.id, f)) return def.id;
  }
  return null;
}

// ───────────────────────────────────────────────────────────── Placement pur

export interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Hauteur de la flèche (px de conception). */
export const TIP_ARROW_H = 9;
/** Écart entre la pointe de la flèche et l'ancre. */
export const TIP_GAP = 3;
/** Marge minimale entre la bulle et le bord de la colonne. */
export const TIP_MARGIN = 8;
/** Distance minimale de la flèche aux coins de la bulle. */
export const TIP_ARROW_PAD = 16;
/** Largeur maximale de la bulle (px de conception). */
export const TIP_MAX_W = 300;

export interface Placement {
  left: number;
  top: number;
  side: 'above' | 'below';
  /** Abscisse de la pointe de la flèche, dans la bulle. */
  arrowX: number;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(Math.max(v, lo), Math.max(lo, hi));
}

/**
 * Rectangle écran (`getBoundingClientRect`) → px de conception de la colonne :
 * on retire l'origine de `#app` puis l'échelle (phase 2, `transform: scale()`).
 */
export function toColumnBox(
  rect: { left: number; top: number; width: number; height: number },
  origin: { left: number; top: number },
  scale: number,
): Box {
  const s = scale > 0 && Number.isFinite(scale) ? scale : 1;
  return {
    left: (rect.left - origin.left) / s,
    top: (rect.top - origin.top) / s,
    width: rect.width / s,
    height: rect.height / s,
  };
}

/**
 * Place la bulle (taille `tip`) par rapport à l'ancre, tout en px de conception
 * de la colonne `col` : au-dessus ou en dessous selon la place (le côté préféré
 * si la bulle y tient, sinon l'autre, sinon celui qui a le plus de place),
 * centrée sur l'ancre puis rabattue dans la colonne ; la flèche garde l'ancre
 * pour cible et ne sort jamais de la bulle.
 */
export function placeTip(
  anchor: Box,
  tip: { width: number; height: number },
  col: { width: number; height: number },
  prefer: 'above' | 'below',
): Placement {
  const need = tip.height + TIP_ARROW_H + TIP_GAP;
  const room = {
    above: anchor.top - TIP_MARGIN,
    below: col.height - (anchor.top + anchor.height) - TIP_MARGIN,
  };
  const other = prefer === 'above' ? 'below' : 'above';
  let side: 'above' | 'below' = prefer;
  if (room[prefer] < need) side = room[other] >= need || room[other] > room[prefer] ? other : prefer;

  const rawTop =
    side === 'above'
      ? anchor.top - TIP_GAP - TIP_ARROW_H - tip.height
      : anchor.top + anchor.height + TIP_GAP + TIP_ARROW_H;
  const top = clamp(rawTop, TIP_MARGIN, col.height - tip.height - TIP_MARGIN);

  const centerX = anchor.left + anchor.width / 2;
  const left = clamp(centerX - tip.width / 2, TIP_MARGIN, col.width - tip.width - TIP_MARGIN);
  const arrowX = clamp(centerX - left, TIP_ARROW_PAD, tip.width - TIP_ARROW_PAD);
  return { left, top, side, arrowX };
}

// ──────────────────────────────────────────────────────────────── Bulle active

interface ActiveTip {
  id: TipId;
  el: HTMLElement;
  def: TipDef;
  /** Cherche l'ancre courante (rectangle écran), ou `null` si elle a disparu. */
  anchor: () => DOMRect | null;
  close: () => void;
}

/** Bulle actuellement affichée (une seule à la fois, toutes écrans confondus). */
let active: ActiveTip | null = null;

/** Identifiant de la bulle affichée, `null` sinon. */
export function currentTip(): TipId | null {
  return active ? active.id : null;
}

/** Y a-t-il quelque chose par-dessus lequel une bulle ne doit pas se poser ? */
function blocked(): boolean {
  return overlayRoot().querySelector('.f-scrim, .f-modal, .update-notice:not([hidden])') !== null;
}

function visibleRect(el: Element | null): DOMRect | null {
  if (!el || !el.isConnected) return null;
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return null;
  if (typeof getComputedStyle === 'function' && getComputedStyle(el).display === 'none') return null;
  return r;
}

/** Échelle de la colonne : rect de `#app` ÷ sa taille de mise en page. */
function columnScale(app: HTMLElement, rect: DOMRect): number {
  return app.offsetWidth > 0 ? rect.width / app.offsetWidth : 1;
}

/** Positionne la bulle ; `false` si l'ancre n'est plus là. */
function place(tip: ActiveTip): boolean {
  const aRect = tip.anchor();
  if (!aRect) return false;
  const app = overlayRoot();
  const appRect = app.getBoundingClientRect();
  const scale = columnScale(app, appRect);
  const anchor = toColumnBox(aRect, appRect, scale);
  const col = { width: app.offsetWidth || appRect.width / scale, height: app.offsetHeight || appRect.height / scale };
  const size = { width: tip.el.offsetWidth, height: tip.el.offsetHeight };
  const p = placeTip(anchor, size, col, tip.def.prefer);
  tip.el.style.left = `${Math.round(p.left * 10) / 10}px`;
  tip.el.style.top = `${Math.round(p.top * 10) / 10}px`;
  tip.el.style.setProperty('--tip-ax', `${Math.round(p.arrowX * 10) / 10}px`);
  if (tip.el.dataset.side !== p.side) tip.el.dataset.side = p.side;
  return true;
}

// ───────────────────────────────────────────────────────────── Faits du jeu

/** Instantané des faits que lisent les conditions, d'après la façade `Game`. */
function readFacts(
  ctx: ScreenCtx,
  screen: TipScreen,
  screenEl: HTMLElement,
  diceJustBought: boolean,
): TipFacts {
  const s = ctx.game.state;
  const equippedIds = s.equipped.filter((id): id is string => typeof id === 'string');
  let cheapest: number | null = null;
  for (const m of MACHINES) {
    // A94 : les Corrupted GachaDice (prix indexé sur le niveau) ne sont pas « la machine la moins chère ».
    if (isCorruptedMachine(m)) continue;
    if (!isMachineUnlocked(s.nodes, m)) continue;
    const cost = ctx.game.gachaCost(m.id, 1);
    if (Number.isFinite(cost) && (cheapest === null || cost < cheapest)) cheapest = cost;
  }
  const early = screenEl.querySelector<HTMLElement>('[data-action="early"]');
  return {
    screen,
    phase: s.phase,
    throws: s.stats.throws,
    runs: s.stats.runs,
    gold: s.gold,
    cheapestMachine: cheapest,
    invasionReady: s.invasionReady,
    diceJustBought,
    unequippedMasters: s.inventory.filter((d) => !equippedIds.includes(d.id)).length,
    equippedMasters: equippedIds.length,
    earlyButton: early !== null && !early.hidden,
  };
}

// ─────────────────────────────────────────────────────────────────── Montage

/** Délai d'attente de l'invite de lancer avant de viser le bas de la zone de combat. */
const HINT_WAIT_MS = 1500;
/** Battement : réessai d'une bulle retardée, repositionnement, ancre disparue. */
const BEAT_MS = 250;

/**
 * Monte le service d'info-bulles d'un écran. À appeler **en fin de montage**
 * (les ancres doivent exister) ; renvoie le démontage, à ajouter à celui de
 * l'écran. Ne fait rien du tout quand les sept bulles ont déjà été vues.
 */
export function mountTips(ctx: ScreenCtx, screen: TipScreen, screenEl: HTMLElement): () => void {
  publishTipsDebug();
  const seen0 = readSeen();
  if (!TIPS.some((t) => t.screen === screen && !seen0.has(t.id))) return () => undefined;

  const { game } = ctx;
  const mountedAt = performance.now();
  /** Dés Maîtres obtenus à la boutique, pas encore passés en revue (bulle `equip`). */
  let diceJustBought = false;
  /** Bulle retenue mais retardée (fenêtre ouverte, ancre pas encore posée). */
  let pending: TipId | null = null;
  let disposed = false;
  let scheduled = false;
  let mine: ActiveTip | null = null;

  function facts(): TipFacts {
    return readFacts(ctx, screen, screenEl, diceJustBought);
  }

  /** Ancre de la bulle : l'élément visé, ou (invite de lancer) le bas de la zone de combat. */
  function anchorOf(def: TipDef): () => DOMRect | null {
    const scope: ParentNode = def.scope === 'app' ? overlayRoot() : screenEl;
    return () => {
      // A100-3 : l'onglet Dés ouvert, la flèche vise son sous-onglet, pas l'onglet.
      const inside = def.anchorInside ? visibleRect(scope.querySelector(def.anchorInside)) : null;
      if (inside) return inside;
      const found = visibleRect(scope.querySelector(def.anchor));
      if (found || def.id !== 'throw') return found;
      // Vue de secours (canevas, aucun élément) ou scène pas encore prête :
      // le bas de la zone de combat, là où l'invite se pose.
      const zone = overlayRoot().querySelector<HTMLElement>('.battle-container');
      const r = visibleRect(zone);
      if (!r || zone?.hidden) return null;
      const h = 24;
      return new DOMRect(r.left, r.top + r.height * 0.82 - h / 2, r.width, h);
    };
  }

  function close(): void {
    if (!mine) return;
    const tip = mine;
    mine = null;
    if (active === tip) active = null;
    tip.close();
  }

  function show(id: TipId): void {
    const def = tipDef(id);
    const anchor = anchorOf(def);
    const el = document.createElement('div');
    el.className = 'f-tip';
    el.setAttribute('role', 'note');
    el.dataset.tip = id;
    el.style.maxWidth = `${TIP_MAX_W}px`;
    const body = document.createElement('div');
    body.className = 'f-tip-body';
    body.textContent = def.text;
    const arrow = document.createElement('i');
    arrow.className = 'f-tip-arrow';
    arrow.setAttribute('aria-hidden', 'true');
    el.append(body, arrow);

    let unregisterEscape = (): void => undefined;
    const tip: ActiveTip = {
      id,
      el,
      def,
      anchor,
      close: () => {
        document.removeEventListener('pointerdown', onPointer, true);
        window.removeEventListener('resize', onMove);
        window.removeEventListener('scroll', onMove, true);
        unregisterEscape();
        el.remove();
      },
    };
    // Tout toucher ferme la bulle, sans arrêter l'événement (ni `preventDefault`).
    function onPointer(): void {
      close();
    }
    function onMove(): void {
      if (mine === tip && !place(tip)) close();
    }
    overlayRoot().appendChild(el);
    if (!place(tip)) {
      el.remove();
      return;
    }
    // Vue dès l'affichage : fermée par n'importe quoi, elle ne revient pas.
    markSeen(id);
    mine = tip;
    active = tip;
    document.addEventListener('pointerdown', onPointer, true);
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    unregisterEscape = registerEscape(el, close);
  }

  /** Affiche la bulle retenue si rien ne l'en empêche (une seule à la fois). */
  function tryShow(): void {
    if (disposed || active || pending === null) return;
    const id = pending;
    const f = facts();
    // La condition a pu tomber entre-temps (dé équipé, partie lancée…).
    if (readSeen().has(id) || !tipApplies(id, f)) {
      if (id === 'equip') diceJustBought = false;
      pending = null;
      return;
    }
    if (blocked()) return;
    const def = tipDef(id);
    // Invite de lancer : on lui laisse le temps de se poser avant de se rabattre
    // sur le bas de la zone de combat.
    if (id === 'throw' && performance.now() - mountedAt < HINT_WAIT_MS) {
      if (!visibleRect(overlayRoot().querySelector(def.anchor))) return;
    }
    if (!anchorOf(def)()) return;
    pending = null;
    show(id);
  }

  /** Montage ou événement : retient la première bulle de la liste, puis tente. */
  function evaluate(): void {
    if (disposed || active) return;
    pending = pickTip(facts(), readSeen());
    tryShow();
  }

  /** Les événements du cœur arrivent en rafale (or, achat, rendu) : une passe, après. */
  function schedule(): void {
    if (scheduled || disposed) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      evaluate();
    });
  }

  const offs: (() => void)[] = [];
  offs.push(game.on('gold', schedule));
  offs.push(
    game.on('purchase', (p) => {
      if (p.dice && p.dice.length > 0) {
        // Premier dé Maître obtenu : le joueur a trouvé la boutique (bulle 4
        // sans objet) et c'est à l'équipement de passer.
        diceJustBought = true;
        markSeen('shop');
      }
      schedule();
    }),
  );
  // Le premier lancer rend la bulle de l'invite caduque (lancer au clavier, auto).
  offs.push(
    game.on('throw', () => {
      if (mine?.id === 'throw') close();
    }),
  );

  const beat = window.setInterval(() => {
    if (disposed) return;
    if (mine) {
      // Fenêtre ouverte par-dessus, ou ancre disparue : la bulle s'efface.
      if (blocked() || !place(mine)) close();
      return;
    }
    tryShow();
  }, BEAT_MS);

  evaluate();

  return () => {
    disposed = true;
    window.clearInterval(beat);
    for (const off of offs) off();
    close();
  };
}

// ───────────────────────────────────────────────────────────────────── Debug

/**
 * Publie `window.__dod.tips` (`seen()`, `reset()`, `current()`) quand le mode
 * debug a posé `window.__dod`. `main.ts` pose cet objet *après* le premier
 * montage : on republie au tour suivant, à chaque montage et à chaque ouverture
 * du panneau Debug.
 */
export function publishTipsDebug(): void {
  if (typeof window === 'undefined') return;
  const put = (): void => {
    const dod = (window as unknown as { __dod?: Record<string, unknown> }).__dod;
    if (!dod) return;
    dod.tips = {
      seen: (): TipId[] => TIP_IDS.filter((id) => readSeen().has(id)),
      reset: (): void => resetSeen(),
      current: (): TipId | null => currentTip(),
    };
  };
  put();
  window.setTimeout(put, 0);
}
